import { router } from 'expo-router'
import { isWeb } from 'tamagui'
import { openWebBrowser } from './openWebBrowser'
import {
  comboPath,
  courseContentPath,
  coursePath,
  heroThemeCourseContentPath,
  homePath,
} from './coursePaths'

interface IProductRedirectToEnrollment {
  enrollmentId: string
  courseId?: string | null
  courseContentId?: number | null
  pathId?: number | null
  kind?: string | null
  baseUrl?: string | null
  hasCourseContentsPage?: boolean
  /** Visão do produtor: navega sob /admin_view, sem matrícula. */
  isPreview?: boolean
}

export const REDIRECT_TO_CONTENT_TYPES = ['ebook', 'file', 'webinar']

export const productRedirectToEnrollment = ({
  enrollmentId,
  courseId,
  courseContentId,
  pathId,
  kind,
  baseUrl,
  hasCourseContentsPage = false,
  isPreview = false,
}: IProductRedirectToEnrollment) => {
  const pathParams = { isPreview, enrollmentId, courseId: courseId ?? '' }
  const isLiveProduct = kind === 'live_meet'
  const shouldRedirectToContent = kind
    ? REDIRECT_TO_CONTENT_TYPES.includes(kind)
    : false
  // Produto ao vivo mora no hero_theme e não tem equivalente em preview.
  const productRedirectUrl = isLiveProduct
    ? `/enrollments/${enrollmentId}/courses/${courseId}`
    : coursePath(pathParams)
  // A tela de consumo do members vive em /online_course/course_contents/:id — é a
  // única rota que o nginx mapeia para members_page_course_contents. A URL sem
  // /online_course só existe no hero_theme, para as escolas ainda sem a flag.
  const membersContentUrl = courseContentPath({
    ...pathParams,
    courseContentId: courseContentId ?? '',
  })
  const heroThemeContentUrl = heroThemeCourseContentPath({
    enrollmentId,
    courseId: courseId ?? '',
    courseContentId: courseContentId ?? '',
  })
  const pathRedirectUrl = comboPath({ isPreview, enrollmentId, pathId })
  // No nativo a tela de consumo é sempre a do members; no web ela depende da
  // flag. Em preview nunca saímos do members: o hero_theme responde 401 para
  // colaborador nas rotas de aluno.
  const shouldLeaveMembers =
    isWeb && !isPreview && shouldRedirectToContent && !hasCourseContentsPage

  const redirect = (url: string) => {
    // Produto ao vivo mora no hero_theme. Web: window.location (mesma origem).
    // Nativo: openWebBrowser com URL absoluta do subdomínio da escola.
    if (isLiveProduct) {
      openWebBrowser(isWeb ? url : `${baseUrl ?? ''}${url}`)
      return
    }

    if (shouldLeaveMembers) {
      window.location.href = url
      return
    }

    router.navigate(url)
  }

  const getRedirectUrl = () => {
    // Sem curso nem trilha a URL sairia /courses/ vazia, que só casa com o
    // [...missing] — a tela "disponível apenas na versão web" (HET-3955).
    if (!courseId && !pathId) return homePath(isPreview)

    if (shouldRedirectToContent) {
      return shouldLeaveMembers ? heroThemeContentUrl : membersContentUrl
    }

    // Combo não tem curso próprio: courses do produto vem vazio e só o pathId
    // aponta para o conteúdo, com ou sem matrícula.
    if (pathId) {
      return pathRedirectUrl
    }

    return productRedirectUrl
  }

  redirect(getRedirectUrl())
}
